import {BaseAreaViewAsync, OBaseAreaViewInit} from "lib/commons/views";
import {IDepotResUiEnv, IDepotUiEnv} from "lib/store/depot";
import {IReg, IRegPointer, REG} from "lib/commons/registry";
import {DOMSH} from "lib/commons/xml/domsh";
import {JSX} from "lib/commons/xml/dom";
import {BarActions, OBarActionsInit} from "back/commons/widgets/bars";
import {IDepotResCtx, RES} from "lib/store/res";
import {AddByImport, ImportRes, RemoveRes, ResEditAction} from "back/store/actions/resActions";
import {JNodeInfos, URLTREE} from "lib/store/urlTree";
import {InfoBrokerBasic} from "lib/commons/infos";
import {POPUP} from "back/commons/widgets/popups";


export interface ResTemplatesView extends BaseAreaViewAsync<IRegPointer<IDepotResUiEnv>> {
	initialize(init: OResTemplatesViewInit): this
}

export interface OResTemplatesViewInit extends OBaseAreaViewInit<IRegPointer<IDepotResUiEnv>> {
	/** si homePrc non renseigné, pas de gestion d'un gabarit d'accueil de dossier. */
	homePrc?: string
	homeTitle?: string
	homeDetails?: HTMLElement

	/** si notFoundPrc non renseigné, pas de gestion d'un gabarit de page 404 not found. */
	notFoundPrc?: string
	notFoundTitle?: string
	notFoundDetails?: HTMLElement

	/** si authPrc non renseigné, pas de gestion d'un gabarit d'accueil. */
	authPrc?: string
	authTitle?: string
	authDetails?: HTMLElement
}

export class ResTemplatesView extends BaseAreaViewAsync<IRegPointer<IDepotResUiEnv>> {
	reg: IReg<IDepotResUiEnv & IDepotUiEnv>;

	childrenQueryNodes: JNodeInfos;

	protected async _initialize(init: OResTemplatesViewInit) {
		this.reg = init.areaContext.reg || this.findReg(init);
		super._initialize(init); //init this.area, this.areaContext
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		await this.fetchChildrenQuery();
		const actionContext = {
			reg: this.reg,
			infoBroker: new InfoBrokerBasic(),
			emitter: this
		};
		const bars: BarActions<IDepotResCtx>[] = [];
		if (init.homePrc) {
			const bar = <BarActions id="homeTplActions" î={{
				reg: this.reg,
				actions: this.getHomeActions(init),
				actionContext,
				uiContext: "dialog",
				disableFullOverlay: true
			} as OBarActionsInit<IDepotResCtx>}/> as BarActions<IDepotResCtx>;
			sr.appendChild(<section id="homeTpl">
				<h3>{init.homeTitle || "Accueil dynamique des dossiers"}</h3>
				{init.homeDetails ||
		<div>
			<p>Cette page gabarit est appliquée sur ce dossier et ses sous-dossiers sauf si un accueil personnalisé du dossier a été importé.</p>
			<p>Elle inclut généralement du code (javascript) permettant de lister le contenu du dossier.</p>
		</div>
				}
				{bar}
			</section>);
			bars.push(bar);
		}

		if (init.notFoundPrc) {
			const bar = <BarActions id="homeTplActions" î={{
				reg: this.reg,
				actions: this.getNotFoundActions(init),
				actionContext,
				uiContext: "dialog",
				disableFullOverlay: true
			} as OBarActionsInit<IDepotResCtx>}/> as BarActions<IDepotResCtx>;
			sr.appendChild(<section id="homeTpl">
				<h3>{init.notFoundTitle || "Page « non trouvée » (404)"}</h3>
				{init.notFoundDetails ||
		<div>
			<p>Cette page est affichée lorsqu'un utilisateur demande un dossier ou une ressource (dans ce dossier ou ses sous-dossiers) qui n'existe pas ou est en état supprimé (erreur 404).</p>
		</div>
				}
				{bar}
			</section>);
			bars.push(bar);
		}

		if (init.authPrc) {
			const bar = <BarActions id="authTplActions" î={{
				reg: this.reg,
				actions: this.getAuthActions(init),
				actionContext,
				uiContext: "dialog",
				disableFullOverlay: true
			} as OBarActionsInit<IDepotResCtx>}/> as BarActions<IDepotResCtx>;
			sr.appendChild(<section id="authTpl">
				<h3>{init.authTitle || "Page d'authentification"}</h3>
				{init.authDetails ||
		<div>
			<p>Cette page est affichée lorsqu'un utilisateur demande une ressource (dans ce dossier ou ses sous-dossiers) sans avoir des droits suffisants (erreur 401 ou 403). Elle permet de gérer la connexion ou les changements de comptes.</p>
		</div>
				}
				{bar}
			</section>);
			bars.push(bar);
		}

		//Refresh
		this.reg.env.nodeInfosChange.on("childChange", async (m) => {
			if (URLTREE.isQueryPath(m.path)) {
				await this.fetchChildrenQuery();
				bars.forEach((bar) => bar.refreshContent());
			}
		});

	}

	getHomeActions(init: OResTemplatesViewInit) {
		return [
			new ImportHiddenRes('homeImport', "?home", init.homePrc).setLabel("Importer un accueil dynamique des dossiers"),
			new DeleteHiddenRes('homeDelete', "?home").setLabel("Supprimer l'accueil dynamique personnalisé")
		]
	}

	getNotFoundActions(init: OResTemplatesViewInit) {
		return [
			new ImportHiddenRes('notFoundImport', "?notFound", init.notFoundPrc).setLabel("Importer une page 404 personnalisée"),
			new DeleteHiddenRes('notFoundDelete', "?notFound").setLabel("Supprimer la page 404 personnalisée")
		]
	}

	getAuthActions(init: OResTemplatesViewInit) {
		return [
			new ImportHiddenRes('authImport', "?authPage", init.authPrc).setLabel("Importer une page d'authentification personnalisée"),
			new DeleteHiddenRes('authDelete', "?authPage").setLabel("Supprimer la page d'authentification personnalisée")
		]
	}

	async fetchChildrenQuery() {
		this.childrenQueryNodes = await this.reg.env.universe.adminUrlTree.listChildren(this.reg.env.path, `&excludeAll&childrenProps=${RES.NODEPROPS_short}&childrenFilter=isQuery`);
	}

}

export class ImportHiddenRes extends ImportRes<IDepotResCtx> {

	constructor(id: string, public subPath: string, public prc: string) {
		super(id);
	}

	isVisible(ctx: IDepotResCtx): boolean {
		return ResEditAction.prototype.isVisible.call(this, ctx);
	}

	async execute(ctx: IDepotResCtx, ev?: Event) {
		const uiCtx = AddByImport.getUiCtx(ctx, ev);
		const files = await AddByImport.pickFiles(false);
		if (files) return ImportRes.doImport(files[0], {
			path: URLTREE.appendToPath(ctx.reg.env.nodeInfos.permaPath, this.subPath),
			processing: this.prc,
			olderResId: "" //pas de check d'update conurrent
		}, ctx, uiCtx);
	}
}


export class DeleteHiddenRes extends ResEditAction<IDepotResCtx> {

	constructor(id: string, public subPath: string) {
		super(id);
		this._description = "Supprimer définitivement ce gabarit (restauration impossible)";
		this._icon = "/@skin@/store/actions/removeRes.svg";
		this._group = "edit";
		//fixme remplacer la permission (pour en avoir une pour chacune des 3 actions)
		this._enablePerms = "action.store#remove.res";
	}

	isVisible(ctx: IDepotResCtx): boolean {
		if (!(ctx.emitter as ResTemplatesView).childrenQueryNodes.ch?.find((n: JNodeInfos) => n.n == this.subPath)) return false;
		return super.isVisible(ctx);
	}

	async execute(ctx: IDepotResCtx, ev?: Event) {
		const uiCtx = ResEditAction.getUiCtx(ctx, ev);
		if (await POPUP.confirm("Supprimer définitivement cette page ?", uiCtx, {okLbl: "Supprimer définitivement"})) {
			return ResEditAction.sendCidUpdate({
				path: URLTREE.appendToPath(ctx.reg.env.nodeInfos.permaPath, this.subPath),
				action: "removeAll"
			}, ctx, uiCtx);
		}
	}
}

REG.reg.registerSkin('store-res-templates', 1, /* language=CSS */ `
	:host {
		display: flex;
		flex-direction: column;
	}

	h3 {
		color: var(--alt1-color);
	}
`);

customElements.define('store-res-templates', ResTemplatesView);